/**
 * Task Import Vuex Actions
 * Async operations for the task import feature.
 *
 * Note: Using taskImportService with mock responses for frontend-first development.
 * API calls will be connected when backend is ready.
 */

import taskImportService from '../services/taskImportService';
import taskImportApiDataSourceService from '../services/taskImportApiDataSourceService';
import taskManagerService from '@/features/Admin/features/TaskManager/service/TaskManager.service';
import Vue from 'vue';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { WIZARD_STEPS } from '../config/taskImportSteps';
import { detectFieldType, extractDropdownOptions } from '../config/taskSystemFields';
import { TaskFieldType } from '@Share/config/enums';
import { transformValueForImport } from '../utils/valueTransformers';

const LOG_PREFIX = '[TaskImport]';

export default {
  // ==========================================================================
  // Wizard Actions
  // ==========================================================================

  /**
   * Open the import wizard.
   */
  OPEN_WIZARD({ commit }) {
    commit('RESET_WIZARD_STATE');
    commit('SET_WIZARD_OPEN', true);
    debugLog(LOG_PREFIX, 'Wizard opened');
  },

  /**
   * Close the import wizard.
   */
  CLOSE_WIZARD({ commit }) {
    commit('SET_WIZARD_OPEN', false);
    commit('RESET_WIZARD_STATE');
    debugLog(LOG_PREFIX, 'Wizard closed');
  },

  /**
   * Go to next wizard step.
   */
  NEXT_STEP({ commit, state }) {
    if (state.wizardStep < WIZARD_STEPS.SUCCESS) {
      commit('SET_WIZARD_STEP', state.wizardStep + 1);
      debugLog(LOG_PREFIX, `Advanced to step ${state.wizardStep + 1}`);
    }
  },

  /**
   * Go to previous wizard step.
   */
  PREV_STEP({ commit, state }) {
    if (state.wizardStep > WIZARD_STEPS.CONFIGURATION) {
      commit('SET_WIZARD_STEP', state.wizardStep - 1);
      debugLog(LOG_PREFIX, `Returned to step ${state.wizardStep - 1}`);
    }
  },

  /**
   * Go to a specific wizard step.
   */
  GO_TO_STEP({ commit, getters }, step) {
    if (getters.CAN_PROCEED_TO_STEP(step)) {
      commit('SET_WIZARD_STEP', step);
      debugLog(LOG_PREFIX, `Jumped to step ${step}`);
    }
  },

  // ==========================================================================
  // Scope & TaskBoard Selection
  // ==========================================================================

  /**
   * Set scope type and fetch available TaskBoards.
   */
  async SET_SCOPE({ commit, dispatch }, { scopeType, targetId }) {
    if (scopeType === 'Facility') {
      commit('SET_SELECTED_FACILITY_ID', targetId);
    } else {
      commit('SET_SELECTED_FACILITY_GROUP_ID', targetId);
    }
    commit('SET_SCOPE_TYPE', scopeType);

    // Fetch available TaskBoards for this scope
    await dispatch('FETCH_TASK_TYPES');
  },

  /**
   * Fetch available TaskBoards for current scope.
   */
  async FETCH_TASK_TYPES({ commit, state }) {
    commit('SET_TASK_TYPES_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const targetId = state.scopeType === 'Facility'
        ? state.selectedFacilityId
        : state.selectedFacilityGroupId;

      if (!targetId) {
        commit('SET_AVAILABLE_TASK_TYPES', []);
        return [];
      }

      const taskTypes = await taskImportService.getTaskTypes({
        scopeType: state.scopeType,
        targetId,
      });

      commit('SET_AVAILABLE_TASK_TYPES', taskTypes);
      debugLog(LOG_PREFIX, `Fetched ${taskTypes.length} TaskBoards`);
      return taskTypes;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch TaskBoards');
      debugError(LOG_PREFIX, 'Failed to fetch TaskBoards:', error);
      throw error;
    } finally {
      commit('SET_TASK_TYPES_LOADING', false);
    }
  },

  /**
   * Select a TaskBoard and load its full schema (sections, fields, statuses).
   * Fetches full details from GET /api/TaskTypes/{id} for field mapping.
   */
  async SELECT_TASK_TYPE({ commit }, taskType) {
    if (!taskType?.id) {
      commit('SET_SELECTED_TASK_TYPE', null);
      return;
    }

    try {
      commit('SET_TASK_TYPES_LOADING', true);
      debugLog(LOG_PREFIX, `Fetching full TaskBoard details: ${taskType.name}`);

      // Fetch full TaskType details (includes sections, fields, statuses)
      const fullTaskType = await taskImportService.getTaskTypeById(taskType.id);

      // Extract custom fields from sections for field mapping
      const customFields = [];
      if (fullTaskType.sections) {
        fullTaskType.sections.forEach(section => {
          if (section.fields) {
            section.fields.forEach(field => {
              customFields.push({
                id: field.id,
                name: field.label || field.name,
                type: field.fieldType,
                section: section.name,
                options: field.options || [],
              });
            });
          }
        });
      }

      // Store full TaskType with customFields for field mapping
      commit('SET_SELECTED_TASK_TYPE', {
        ...fullTaskType,
        customFields, // Flattened fields for GET_TASK_TYPE_CUSTOM_FIELDS getter
      });

      debugLog(LOG_PREFIX, `Selected TaskBoard: ${fullTaskType.name} with ${customFields.length} custom fields`);
    } catch (error) {
      debugError(LOG_PREFIX, 'Failed to fetch TaskBoard details:', error);
      // Fallback to basic info if fetch fails
      commit('SET_SELECTED_TASK_TYPE', taskType);
    } finally {
      commit('SET_TASK_TYPES_LOADING', false);
    }
  },

  // ==========================================================================
  // Inline API Test & Preview (for dataSourceType === 'api')
  // ==========================================================================

  /**
   * Test connection to an external API.
   * Uses the task import API data source service.
   */
  async TEST_API_CONNECTION({ commit, state }) {
    commit('SET_API_TEST_RESULT', null);
    try {
      const connectionConfig = {
        url: state.apiConfig.url,
        httpMethod: state.apiConfig.httpMethod,
        requestBody: state.apiConfig.requestBody || null,
        headers: (state.apiConfig.headers || []).filter(h => h.key),
        auth: state.apiConfig.auth,
        responseDataPath: state.apiConfig.responseDataPath || null,
        pagination: state.apiConfig.pagination,
      };
      const result = await taskImportApiDataSourceService.testConnection(connectionConfig);
      commit('SET_API_TEST_RESULT', result);
      debugLog(LOG_PREFIX, 'API connection test:', result.success ? 'success' : 'failed');
      return result;
    } catch (err) {
      const errorResult = { success: false, errorMessage: err.message || 'Connection test failed' };
      commit('SET_API_TEST_RESULT', errorResult);
      debugError(LOG_PREFIX, 'API connection test failed:', err);
      return errorResult;
    }
  },

  /**
   * Fetch preview data from an external API.
   * On success, pushes headers/data into parsedData/excelColumns for FieldMappingStep.
   */
  async FETCH_API_PREVIEW({ commit, state, dispatch }, { signal } = {}) {
    commit('SET_API_PREVIEW_DATA', null);
    try {
      const connectionConfig = {
        url: state.apiConfig.url,
        httpMethod: state.apiConfig.httpMethod,
        requestBody: state.apiConfig.requestBody || null,
        headers: (state.apiConfig.headers || []).filter(h => h.key),
        auth: state.apiConfig.auth,
        responseDataPath: state.apiConfig.responseDataPath || null,
        pagination: state.apiConfig.pagination,
      };
      const result = await taskImportApiDataSourceService.fetchPreview(connectionConfig, 20, { signal });
      commit('SET_API_PREVIEW_DATA', result);

      if (result.success && result.headers && result.data) {
        // Push API preview data into the same store fields as Excel upload
        // so FieldMappingStep works identically
        commit('SET_EXCEL_COLUMNS', result.headers);
        commit('SET_PARSED_DATA', result.data);
        commit('SET_SOURCE_FILE_NAME', `API: ${state.apiConfig.name || state.apiConfig.url}`);

        // Trigger auto-mapping
        dispatch('AUTO_MAP_FIELDS');
      }

      debugLog(LOG_PREFIX, 'API preview:', result.success ? `${result.data?.length || 0} rows` : 'failed');
      return result;
    } catch (err) {
      const errorResult = { success: false, errorMessage: err.message || 'Preview fetch failed' };
      commit('SET_API_PREVIEW_DATA', errorResult);
      debugError(LOG_PREFIX, 'API preview failed:', err);
      return errorResult;
    }
  },

  // ==========================================================================
  // File Upload
  // ==========================================================================

  /**
   * Set parsed Excel data and columns.
   * Auto-generates board name for new mode and attempts auto-mapping.
   */
  SET_EXCEL_DATA({ commit, state, dispatch }, { data, columns, fileName }) {
    commit('SET_PARSED_DATA', data);
    commit('SET_EXCEL_COLUMNS', columns);
    commit('SET_SOURCE_FILE_NAME', fileName);

    // Auto-generate board name from filename for new mode
    if (state.importMode === 'new' && fileName) {
      const baseName = fileName.replace(/\.[^/.]+$/, ''); // Remove extension
      const date = new Date().toISOString().split('T')[0];
      commit('SET_NEW_BOARD_NAME', `${baseName} - ${date}`);
    }

    debugLog(LOG_PREFIX, `Loaded ${data.length} rows with ${columns.length} columns from ${fileName}`);

    // Attempt auto-mapping
    dispatch('AUTO_MAP_FIELDS');

    // If new board mode, detect custom field types
    if (state.importMode === 'new') {
      dispatch('DETECT_CUSTOM_FIELDS');
    }
  },

  /**
   * Auto-map Excel columns to system fields based on name similarity.
   */
  AUTO_MAP_FIELDS({ commit, state, getters }) {
    const mappings = {};
    const columns = state.excelColumns.map(c => c.toLowerCase());

    // Common aliases for system fields
    const fieldAliases = {
      text: ['title', 'name', 'task', 'task name', 'task title', 'subject'],
      description: ['description', 'desc', 'details', 'notes', 'note'],
      status: ['status', 'state', 'stage'],
      progress: ['progress', 'percent', 'completion', '%', 'done'],
      dueDate: ['due date', 'due', 'deadline', 'end date', 'target date'],
      startDate: ['start date', 'start', 'begin date', 'from'],
      priority: ['priority', 'importance', 'urgency'],
      tags: ['tags', 'tag', 'labels', 'label', 'forge tags', 'linked tags'],
    };

    Object.entries(fieldAliases).forEach(([fieldKey, aliases]) => {
      for (const alias of aliases) {
        const colIndex = columns.findIndex(c =>
          c === alias || c.includes(alias) || alias.includes(c)
        );
        if (colIndex !== -1 && !Object.values(mappings).includes(state.excelColumns[colIndex])) {
          mappings[fieldKey] = state.excelColumns[colIndex];
          break;
        }
      }
    });

    commit('SET_SYSTEM_FIELD_MAPPINGS', mappings);
    debugLog(LOG_PREFIX, `Auto-mapped ${Object.keys(mappings).length} system fields`);
  },

  /**
   * Detect custom field types from Excel data (for new board mode).
   */
  DETECT_CUSTOM_FIELDS({ commit, state, getters }) {
    const unmapped = getters.GET_UNMAPPED_COLUMNS;
    const customFields = [];

    unmapped.forEach(column => {
      // Get sample values from this column
      const values = state.parsedData
        .slice(0, 100) // Sample first 100 rows
        .map(row => row[column])
        .filter(v => v !== null && v !== undefined);

      const dataType = detectFieldType(values);
      const field = {
        excelColumn: column,
        name: column, // camelCase - API auto-converts to PascalCase (Name)
        dataType,
        section: null, // User will organize later
        options: dataType === TaskFieldType.Dropdown ? extractDropdownOptions(values) : null,
      };

      customFields.push(field);
    });

    commit('SET_NEW_CUSTOM_FIELDS', customFields);
    debugLog(LOG_PREFIX, `Detected ${customFields.length} custom fields`);
  },

  // ==========================================================================
  // Duplicate Checking
  // ==========================================================================

  /**
   * Check for duplicate tasks based on key column.
   */
  async CHECK_DUPLICATES({ commit, state, getters }) {
    if (!state.selectedTaskTypeId) {
      return null;
    }

    commit('SET_CHECKING_DUPLICATES', true);
    commit('CLEAR_ERROR');

    try {
      const titleColumn = state.systemFieldMappings['text'];
      const titles = getters.GET_VALID_ROWS
        .map(row => row[titleColumn])
        .filter(Boolean);

      if (titles.length === 0) {
        commit('SET_DUPLICATE_CHECK_RESULT', {
          totalChecked: 0,
          duplicatesFound: 0,
          duplicateTitles: [],
        });
        return;
      }

      const result = await taskImportService.checkDuplicates({
        taskTypeId: state.selectedTaskTypeId,
        titles,
      });

      commit('SET_DUPLICATE_CHECK_RESULT', result);
      debugLog(LOG_PREFIX, `Duplicate check: ${result.duplicatesFound} found`);
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to check duplicates');
      debugError(LOG_PREFIX, 'Duplicate check failed:', error);
      throw error;
    } finally {
      commit('SET_CHECKING_DUPLICATES', false);
    }
  },

  // ==========================================================================
  // User Email Resolution (for Users field type)
  // ==========================================================================

  /**
   * Resolve user emails to user IDs for Users type fields.
   * Called during import preparation when Users fields are mapped.
   * Uses lenient validation - unresolved emails result in warnings, not failures.
   */
  async RESOLVE_USER_EMAILS({ commit, state, getters }) {
    // Get all mapped fields with Users type
    const usersFieldColumns = [];

    // Check existing board custom fields
    if (state.importMode === 'existing' && state.selectedTaskType?.customFields) {
      state.selectedTaskType.customFields.forEach(field => {
        if (field.type === TaskFieldType.Users) {
          const mappedColumn = state.customFieldMappings[field.id];
          if (mappedColumn) {
            usersFieldColumns.push(mappedColumn);
          }
        }
      });
    }

    // Check new board section fields
    if (state.importMode === 'new') {
      state.taskBoardSections.forEach(section => {
        (section.fields || []).forEach(field => {
          if (field.fieldType === TaskFieldType.Users && field.excelColumn) {
            usersFieldColumns.push(field.excelColumn);
          }
        });
      });
    }

    if (usersFieldColumns.length === 0) {
      // No Users fields mapped, nothing to resolve
      commit('SET_USER_EMAIL_MAPPINGS', {});
      commit('SET_UNRESOLVED_USER_EMAILS', []);
      return { mappings: {}, resolved: 0, unresolved: [] };
    }

    commit('SET_RESOLVING_USERS', true);
    commit('CLEAR_ERROR');

    try {
      // Collect all unique emails from Users columns
      const validRows = getters.GET_VALID_ROWS;
      const allEmails = [];

      usersFieldColumns.forEach(column => {
        validRows.forEach(row => {
          const value = row[column];
          if (value && typeof value === 'string' && value.trim()) {
            // Handle potential comma-separated emails
            const emails = value.split(',').map(e => e.trim()).filter(Boolean);
            allEmails.push(...emails);
          }
        });
      });

      // Resolve emails
      const result = await taskImportService.resolveUserEmails(allEmails);

      commit('SET_USER_EMAIL_MAPPINGS', result.mappings);
      commit('SET_UNRESOLVED_USER_EMAILS', result.unresolved);

      debugLog(LOG_PREFIX, `User email resolution: ${result.resolved} resolved, ${result.unresolved.length} unresolved`);

      return result;
    } catch (err) {
      debugWarn(LOG_PREFIX, 'Failed to resolve user emails:', err);
      commit('SET_ERROR', err.message || 'Failed to resolve user emails');
      throw err;
    } finally {
      commit('SET_RESOLVING_USERS', false);
    }
  },

  // ==========================================================================
  // Import Operations
  // ==========================================================================

  /**
   * Build import request from current state.
   * For 'new' mode, includes full TaskType structure from configured sections.
   */
  BUILD_IMPORT_REQUEST({ state, getters }) {
    const validRows = getters.GET_VALID_ROWS;

    // Build field mapping lookup for new board mode (from configured sections)
    // Maps excelColumn to { sectionIndex, fieldIndex, fieldId }
    const sectionFieldMappings = {};
    if (state.importMode === 'new' && state.taskBoardSections.length > 0) {
      state.taskBoardSections.forEach((section, sIndex) => {
        (section.fields || []).forEach((field, fIndex) => {
          if (field.excelColumn) {
            sectionFieldMappings[field.excelColumn] = {
              sectionIndex: sIndex,
              fieldIndex: fIndex,
              fieldId: field.id,
              fieldLabel: field.label,
            };
          }
        });
      });
    }

    // Transform rows to import records
    const records = validRows.map(row => {
      const systemFields = {};
      Object.entries(state.systemFieldMappings).forEach(([fieldKey, excelCol]) => {
        if (excelCol && row[excelCol] !== undefined) {
          // Tags: send as tagNames string for backend to resolve to IDs
          if (fieldKey === 'tags') {
            systemFields.tagNames = row[excelCol];
          } else {
            systemFields[fieldKey] = row[excelCol];
          }
        }
      });

      const customFields = [];

      // For existing boards, use customFieldMappings with value transformation
      if (state.importMode === 'existing') {
        // Get field definitions for type info
        const fieldDefs = state.selectedTaskType?.customFields || [];

        Object.entries(state.customFieldMappings).forEach(([fieldId, excelCol]) => {
          if (excelCol && row[excelCol] !== undefined) {
            // Find field definition to get type and options
            const fieldDef = fieldDefs.find(f => f.id === fieldId);
            const fieldType = fieldDef?.type || TaskFieldType.Text;
            const options = fieldDef?.options || [];

            // Transform value to backend-expected format
            const transformedValue = transformValueForImport(
              row[excelCol],
              fieldType,
              options,
              state.userEmailMappings
            );

            // Only add if transformation succeeded (null = skip)
            if (transformedValue !== null) {
              customFields.push({
                fieldId,
                value: transformedValue,
              });
            }
          }
        });
      }
      // For new boards, use section field mappings with value transformation
      else if (state.importMode === 'new') {
        // Build field lookup from sections (for type and options)
        const sectionFieldDefs = {};
        state.taskBoardSections.forEach(section => {
          (section.fields || []).forEach(field => {
            sectionFieldDefs[field.id] = {
              fieldType: field.fieldType,
              options: field.options || [],
            };
          });
        });

        Object.entries(sectionFieldMappings).forEach(([excelCol, mapping]) => {
          if (row[excelCol] !== undefined) {
            // Get field definition for type and options
            const fieldDef = sectionFieldDefs[mapping.fieldId];
            const fieldType = fieldDef?.fieldType || TaskFieldType.Text;
            const options = fieldDef?.options || [];

            // Transform value to backend-expected format
            const transformedValue = transformValueForImport(
              row[excelCol],
              fieldType,
              options,
              state.userEmailMappings
            );

            // Only add if transformation succeeded (null = skip)
            if (transformedValue !== null) {
              customFields.push({
                fieldId: mapping.fieldId,
                value: transformedValue,
              });
            }
          }
        });
      }

      return { systemFields, customFields };
    });

    // Build TaskType structure for new board (matches TaskManager format)
    let taskTypePayload = null;
    if (state.importMode === 'new') {
      taskTypePayload = {
        name: state.newBoardName,
        description: state.newBoardDescription || '',
        // Scope (mutually exclusive - ForgeBaseTag pattern)
        facilityId: state.scopeType === 'Facility' ? state.selectedFacilityId : null,
        facilityGroupId: state.scopeType === 'FacilityGroup' ? state.selectedFacilityGroupId : null,
        // Sections with fields (from Configure TaskBoard dialog)
        sections: state.taskBoardSections.map((section, index) => ({
          id: section.id,
          name: section.name,
          order: index + 1,
          fields: (section.fields || []).map(field => ({
            id: field.id,
            label: field.label,
            placeHolder: field.placeHolder || '',
            fieldType: field.fieldType,
            color: field.color || '#03624c',
            isEnabled: true,
            isVisible: true,
            options: field.options || [],
          })),
        })),
        // Default statuses (matching TaskManager pattern)
        statuses: [
          { name: 'To Do', statusType: 1, order: 1, color: '#6c757d' },
          { name: 'In Progress', statusType: 2, order: 2, color: '#ffc107' },
          { name: 'Done', statusType: 3, order: 3, color: '#28a745' },
        ],
      };
    }

    const request = {
      // Mode
      importMode: state.importMode,

      // Existing board
      taskTypeId: state.importMode === 'existing' ? state.selectedTaskTypeId : null,

      // New board - full TaskType structure
      newTaskTypeName: state.importMode === 'new' ? state.newBoardName : null,
      newTaskTypeDescription: state.importMode === 'new' ? state.newBoardDescription : null,
      taskTypePayload: taskTypePayload, // Full TaskType structure for backend
      // Legacy schema field for backwards compatibility
      schema: state.importMode === 'new' ? state.newCustomFields : null,

      // Scope (mutually exclusive)
      facilityId: state.scopeType === 'Facility' ? state.selectedFacilityId : null,
      facilityGroupId: state.scopeType === 'FacilityGroup' ? state.selectedFacilityGroupId : null,

      // Duplicate handling
      keyColumn: state.keyColumn,
      duplicateHandling: state.duplicateHandling,

      // Data
      records,
      sourceFileName: state.sourceFileName,
    };

    return request;
  },

  /**
   * Import tasks (existing board mode).
   */
  async IMPORT_TASKS({ commit, state, getters, dispatch }) {
    commit('SET_IMPORTING', true);
    commit('CLEAR_ERROR');

    try {
      const request = await dispatch('BUILD_IMPORT_REQUEST');
      debugLog(LOG_PREFIX, `Importing ${request.records.length} tasks`, request);

      const result = await taskImportService.importTasks(request);

      commit('SET_IMPORT_RESULT', result);

      // Show notification based on result status
      if (result.status === 'Success') {
        Vue.prototype.$toast?.success(`Successfully imported ${result.createdCount} tasks`);
      } else if (result.status === 'PartialSuccess') {
        Vue.prototype.$toast?.warning(
          `Imported ${result.createdCount} tasks, ${result.updatedCount} updated, ${result.failedCount} failed`
        );
      } else {
        Vue.prototype.$toast?.error('Import failed');
      }

      // Move to success step
      commit('SET_WIZARD_STEP', WIZARD_STEPS.SUCCESS);

      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to import tasks');
      Vue.prototype.$toast?.error(error.message || 'Import failed');
      debugError(LOG_PREFIX, 'Import failed:', error);
      throw error;
    } finally {
      commit('SET_IMPORTING', false);
    }
  },

  /**
   * Create new TaskBoard and import tasks.
   */
  async CREATE_BOARD_AND_IMPORT({ commit, state, getters, dispatch }) {
    commit('SET_IMPORTING', true);
    commit('CLEAR_ERROR');

    try {
      const request = await dispatch('BUILD_IMPORT_REQUEST');
      debugLog(LOG_PREFIX, `Creating board "${request.newTaskTypeName}" and importing ${request.records.length} tasks`);

      const result = await taskImportService.createBoardAndImport(request);

      commit('SET_IMPORT_RESULT', result);

      // Show notification
      if (result.status === 'Success') {
        Vue.prototype.$toast?.success(
          `Created board "${result.taskTypeName}" with ${result.createdCount} tasks`
        );
      } else if (result.status === 'PartialSuccess') {
        Vue.prototype.$toast?.warning(
          `Created board with ${result.createdCount} tasks, ${result.failedCount} failed`
        );
      } else {
        Vue.prototype.$toast?.error('Failed to create board and import');
      }

      // Move to success step
      commit('SET_WIZARD_STEP', WIZARD_STEPS.SUCCESS);

      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create board and import');
      Vue.prototype.$toast?.error(error.message || 'Operation failed');
      debugError(LOG_PREFIX, 'Create and import failed:', error);
      throw error;
    } finally {
      commit('SET_IMPORTING', false);
    }
  },

  /**
   * Execute import based on current mode.
   */
  async EXECUTE_IMPORT({ state, dispatch }) {
    if (state.importMode === 'existing') {
      return dispatch('IMPORT_TASKS');
    } else {
      return dispatch('CREATE_BOARD_AND_IMPORT');
    }
  },

  // ==========================================================================
  // Export Operations
  // ==========================================================================

  /**
   * Export tasks from a TaskBoard to CSV.
   */
  async EXPORT_TASKS({ commit }, { taskTypeId, options = {} }) {
    commit('SET_EXPORTING', true);
    commit('CLEAR_ERROR');

    try {
      const blob = await taskImportService.exportTasks(taskTypeId, options);

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tasks-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      commit('SET_EXPORT_RESULT', { success: true, taskTypeId });
      Vue.prototype.$toast?.success('Tasks exported successfully');

      debugLog(LOG_PREFIX, `Exported tasks from TaskBoard ${taskTypeId}`);
      return true;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to export tasks');
      commit('SET_EXPORT_RESULT', { success: false, error: error.message });
      Vue.prototype.$toast?.error(error.message || 'Export failed');
      debugError(LOG_PREFIX, 'Export failed:', error);
      throw error;
    } finally {
      commit('SET_EXPORTING', false);
    }
  },

  // ==========================================================================
  // Import History Sidebar
  // ==========================================================================

  /**
   * Open the import history sidebar.
   * Fetches initial history data.
   */
  async OPEN_HISTORY_SIDEBAR({ commit, dispatch }) {
    commit('SET_HISTORY_SIDEBAR_OPEN', true);
    commit('RESET_HISTORY_FILTERS');
    commit('CLEAR_IMPORT_HISTORY');

    // Fetch initial history
    await dispatch('FETCH_ALL_IMPORT_HISTORY');

    debugLog(LOG_PREFIX, 'History sidebar opened');
  },

  /**
   * Close the import history sidebar.
   */
  CLOSE_HISTORY_SIDEBAR({ commit }) {
    commit('SET_HISTORY_SIDEBAR_OPEN', false);
    debugLog(LOG_PREFIX, 'History sidebar closed');
  },

  /**
   * Fetch import history for a specific TaskBoard.
   */
  async FETCH_IMPORT_HISTORY({ commit }, { taskTypeId, pagination = {} }) {
    commit('SET_HISTORY_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await taskImportService.getImportHistory(taskTypeId, pagination);

      commit('SET_IMPORT_HISTORY', result.items || []);
      commit('SET_IMPORT_HISTORY_TOTAL', result.total || 0);

      debugLog(LOG_PREFIX, `Fetched ${result.items?.length || 0} import history records`);
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch import history');
      debugError(LOG_PREFIX, 'Failed to fetch history:', error);
      throw error;
    } finally {
      commit('SET_HISTORY_LOADING', false);
    }
  },

  /**
   * Fetch all import history for the sidebar.
   * Supports filtering by status, date range.
   */
  async FETCH_ALL_IMPORT_HISTORY({ commit, state, rootGetters }) {
    commit('SET_HISTORY_LOADING', true);
    commit('CLEAR_ERROR');
    commit('SET_HISTORY_FILTERS', { page: 1 }); // Reset page to 1 on refresh

    try {
      // Build filters from state
      const filters = {
        page: state.historyFilters.page,
        itemsPerPage: state.historyFilters.itemsPerPage,
        status: state.historyFilters.status,
        fromDate: state.historyFilters.fromDate,
        toDate: state.historyFilters.toDate,
      };

      // Add scope filters if available
      const facilityId = rootGetters['CURRENT_FACILITY'];
      const facilityGroupId = rootGetters['CURRENT_FACILITY_GROUP'];
      if (facilityId) filters.facilityId = facilityId;
      if (facilityGroupId) filters.facilityGroupId = facilityGroupId;

      const result = await taskImportService.getAllImportHistory(filters);

      commit('SET_IMPORT_HISTORY', result.items || []);
      commit('SET_IMPORT_HISTORY_TOTAL', result.total || 0);

      debugLog(LOG_PREFIX, `Fetched ${result.items?.length || 0} import history records`);
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch import history');
      debugError(LOG_PREFIX, 'Failed to fetch history:', error);
      throw error;
    } finally {
      commit('SET_HISTORY_LOADING', false);
    }
  },

  /**
   * Load more history items (pagination).
   * Appends to existing list.
   */
  async LOAD_MORE_HISTORY({ commit, state, rootGetters }) {
    if (state.historyLoading) return;

    commit('SET_HISTORY_LOADING', true);

    try {
      const nextPage = state.historyFilters.page + 1;

      const filters = {
        page: nextPage,
        itemsPerPage: state.historyFilters.itemsPerPage,
        status: state.historyFilters.status,
        fromDate: state.historyFilters.fromDate,
        toDate: state.historyFilters.toDate,
      };

      // Add scope filters (matching FETCH_ALL_IMPORT_HISTORY)
      const facilityId = rootGetters['CURRENT_FACILITY'];
      const facilityGroupId = rootGetters['CURRENT_FACILITY_GROUP'];
      if (facilityId) filters.facilityId = facilityId;
      if (facilityGroupId) filters.facilityGroupId = facilityGroupId;

      const result = await taskImportService.getAllImportHistory(filters);

      // Append new items to existing list
      commit('APPEND_IMPORT_HISTORY', result.items || []);
      commit('SET_HISTORY_FILTERS', { page: nextPage });

      // Update total to ensure Load More button hides when no more items
      // Edge case: If API returns 0 items, force total to match current loaded count
      // (handles backend count/data inconsistency where total > actual items)
      const newItemsCount = result.items?.length || 0;
      if (newItemsCount === 0) {
        // No more items returned - set total to current length to hide button
        commit('SET_IMPORT_HISTORY_TOTAL', state.importHistory.length);
      } else if (result.total !== undefined) {
        commit('SET_IMPORT_HISTORY_TOTAL', result.total);
      }

      debugLog(LOG_PREFIX, `Loaded ${result.items?.length || 0} more history items`);
      return result;
    } catch (error) {
      debugError(LOG_PREFIX, 'Failed to load more history:', error);
      throw error;
    } finally {
      commit('SET_HISTORY_LOADING', false);
    }
  },

  /**
   * Update history filters and refetch.
   */
  async UPDATE_HISTORY_FILTERS({ commit, dispatch }, filters) {
    // Reset to page 1 when filters change
    commit('SET_HISTORY_FILTERS', { ...filters, page: 1 });
    commit('CLEAR_IMPORT_HISTORY');

    await dispatch('FETCH_ALL_IMPORT_HISTORY');
  },

  /**
   * Delete a specific import log entry.
   */
  async DELETE_IMPORT_LOG({ commit, dispatch }, logId) {
    commit('SET_HISTORY_LOADING', true);

    try {
      await taskImportService.deleteImportLog(logId);

      Vue.prototype.$toast?.success('Import log deleted');
      debugLog(LOG_PREFIX, 'Deleted import log', { logId });

      // Refresh history list
      await dispatch('FETCH_ALL_IMPORT_HISTORY');
    } catch (error) {
      Vue.prototype.$toast?.error('Failed to delete log');
      debugError(LOG_PREFIX, 'Failed to delete log:', error);
      throw error;
    } finally {
      commit('SET_HISTORY_LOADING', false);
    }
  },

  /**
   * Clear old import logs.
   */
  async CLEAR_OLD_IMPORT_LOGS({ commit, dispatch }, { olderThanDays = 90 } = {}) {
    commit('SET_HISTORY_LOADING', true);

    try {
      const deletedCount = await taskImportService.clearOldImportLogs({ olderThanDays });

      Vue.prototype.$toast?.success(`Cleared ${deletedCount} old logs`);
      debugLog(LOG_PREFIX, `Cleared ${deletedCount} old logs`);

      // Refresh history list
      await dispatch('FETCH_ALL_IMPORT_HISTORY');

      return deletedCount;
    } catch (error) {
      Vue.prototype.$toast?.error('Failed to clear logs');
      debugError(LOG_PREFIX, 'Failed to clear logs:', error);
      throw error;
    } finally {
      commit('SET_HISTORY_LOADING', false);
    }
  },

  // ==========================================================================
  // API Data Source Operations
  // ==========================================================================

  /**
   * Fetch API data sources for a TaskBoard.
   */
  async FETCH_API_DATA_SOURCES({ commit }, { taskTypeId }) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      const result = await taskImportApiDataSourceService.getDataSources({ taskTypeId });
      commit('SET_API_DATA_SOURCES', result.items || result);
      commit('SET_API_DATA_SOURCES_TOTAL', result.total || (Array.isArray(result) ? result.length : 0));
      debugLog(LOG_PREFIX, `Fetched ${(result.items || result).length} API data sources`);
      return result;
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to fetch API data sources:', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Create a new API data source for a TaskBoard.
   */
  async CREATE_API_DATA_SOURCE({ commit }, data) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      const created = await taskImportApiDataSourceService.createDataSource(data);
      commit('ADD_API_DATA_SOURCE', created);
      debugLog(LOG_PREFIX, 'Created API data source:', created.name);
      return created;
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to create API data source:', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Create a new TaskBoard and then create an API data source linked to it.
   * Two-step process:
   *   1. Create TaskBoard via POST /api/TaskTypes
   *   2. Create API data source via POST /api/task-import-api-sources with the new board's ID
   *
   * Also remaps temporary field IDs in customFieldMappings to real IDs
   * assigned by the backend during board creation.
   */
  async CREATE_BOARD_WITH_API_SOURCE({ commit, state }, apiPayload) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      // Step 1: Build and create the TaskBoard
      const boardPayload = {
        name: state.newBoardName,
        description: state.newBoardDescription || '',
        facilityId: state.scopeType === 'Facility' ? state.selectedFacilityId : null,
        facilityGroupId: state.scopeType === 'FacilityGroup' ? state.selectedFacilityGroupId : null,
        sections: state.taskBoardSections.map((section, index) => ({
          id: section.id,
          name: section.name,
          order: index + 1,
          fields: (section.fields || []).map(field => ({
            id: field.id,
            label: field.label,
            placeHolder: field.placeHolder || '',
            fieldType: field.fieldType,
            color: field.color || '#03624c',
            isEnabled: true,
            isVisible: true,
            options: field.options || [],
          })),
        })),
        statuses: [
          { name: 'To Do', statusType: 1, order: 1, color: '#6c757d' },
          { name: 'In Progress', statusType: 2, order: 2, color: '#ffc107' },
          { name: 'Done', statusType: 3, order: 3, color: '#28a745' },
        ],
      };

      debugLog(LOG_PREFIX, 'Creating TaskBoard:', boardPayload.name);
      const boardResponse = await taskManagerService.add(boardPayload);
      const createdBoard = boardResponse.data;

      if (!createdBoard?.id) {
        throw new Error('TaskBoard creation did not return an ID');
      }

      debugLog(LOG_PREFIX, 'TaskBoard created:', createdBoard.id);

      // Step 2: Remap temp field IDs in customFieldMappings to real IDs
      // The backend assigns real IDs to fields during creation.
      // Match by label+section position to map temp → real.
      if (apiPayload.mapping?.customFieldMappings) {
        const tempToReal = {};

        // Build temp→real map from original sections (temp IDs) and created board (real IDs)
        const origSections = state.taskBoardSections;
        const realSections = createdBoard.sections || [];

        origSections.forEach((origSection, sIdx) => {
          const realSection = realSections[sIdx];
          if (!realSection) return;

          (origSection.fields || []).forEach((origField, fIdx) => {
            const realField = (realSection.fields || [])[fIdx];
            if (realField && origField.id) {
              tempToReal[origField.id] = realField.id;
            }
          });
        });

        // Remap the keys
        const remapped = {};
        Object.entries(apiPayload.mapping.customFieldMappings).forEach(([tempId, apiColumn]) => {
          const realId = tempToReal[tempId] || tempId;
          remapped[realId] = apiColumn;
        });
        apiPayload.mapping.customFieldMappings = remapped;

        debugLog(LOG_PREFIX, `Remapped ${Object.keys(tempToReal).length} field IDs`);
      }

      // Step 3: Create the API data source against the new board
      apiPayload.taskTypeId = createdBoard.id;

      const created = await taskImportApiDataSourceService.createDataSource(apiPayload);
      commit('ADD_API_DATA_SOURCE', created);

      debugLog(LOG_PREFIX, 'Created board and API data source:', createdBoard.name, created.name);
      return { taskBoard: createdBoard, apiDataSource: created };
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to create board with API source:', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Update an existing API data source.
   */
  async UPDATE_API_DATA_SOURCE({ commit }, { id, data }) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      const updated = await taskImportApiDataSourceService.updateDataSource(id, data);
      commit('UPDATE_API_DATA_SOURCE', updated);
      debugLog(LOG_PREFIX, 'Updated API data source:', updated.name);
      return updated;
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to update API data source:', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Delete an API data source.
   */
  async DELETE_API_DATA_SOURCE({ commit }, id) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      await taskImportApiDataSourceService.deleteDataSource(id);
      commit('REMOVE_API_DATA_SOURCE', id);
      debugLog(LOG_PREFIX, 'Deleted API data source:', id);
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to delete API data source:', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Trigger a manual data pull for an API data source.
   */
  async TRIGGER_API_MANUAL_PULL({ commit }, id) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      const result = await taskImportApiDataSourceService.triggerManualPull(id);
      debugLog(LOG_PREFIX, 'Triggered manual pull for:', id);
      return result;
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to trigger manual pull:', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Toggle schedule on/off for an API data source.
   */
  async TOGGLE_API_DATA_SOURCE_SCHEDULE({ commit }, { id, enabled }) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      const result = await taskImportApiDataSourceService.toggleSchedule(id, enabled);
      commit('UPDATE_API_DATA_SOURCE', result);
      debugLog(LOG_PREFIX, `${enabled ? 'Enabled' : 'Disabled'} schedule for:`, id);
      return result;
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to toggle schedule:', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Fetch execution history for an API data source.
   */
  async FETCH_API_EXECUTION_HISTORY({ commit }, { dataSourceId, params }) {
    commit('SET_API_EXECUTION_HISTORY_LOADING', true);
    try {
      const result = await taskImportApiDataSourceService.getExecutionHistory(dataSourceId, params);
      commit('SET_API_EXECUTION_HISTORY', result.items || result);
      commit('SET_API_EXECUTION_HISTORY_TOTAL', result.total || 0);
      debugLog(LOG_PREFIX, `Fetched ${(result.items || result).length} execution history entries`);
      return result;
    } catch (err) {
      debugError(LOG_PREFIX, 'Failed to fetch execution history:', err);
      throw err;
    } finally {
      commit('SET_API_EXECUTION_HISTORY_LOADING', false);
    }
  },
};
